import * as React from 'react';
import { Image, Loader2, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import type { IPostCardProps } from '@/@types/posts';

function PostCard({
  post,
  isSelected,
  onSelect,
  onClick,
  onEdit,
  onDelete,
  onCancel,
}: IPostCardProps) {
  const isGenerating = post.status === 'generating' || post.status === 'pending';
  const needsImage = post.status === 'completed' && !post.hasGeneratedImage;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleCheckboxChange = (checked: boolean) => {
    onSelect(post._id, checked);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on checkbox or action buttons
    const target = e.target as HTMLElement;
    if (target.closest('[data-action]') || target.closest('[data-slot="checkbox"]')) {
      return;
    }
    onClick(post._id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(post._id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(post._id);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCancel?.(post._id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(post._id);
        }
      }}
      className={cn(
        'flex items-center gap-4 rounded-xl bg-background-card p-4 transition-colors cursor-pointer',
        isGenerating ? 'border border-secondary' : 'border border-border hover:border-border-hover'
      )}
    >
      {/* Checkbox */}
      <Checkbox
        checked={isSelected}
        onCheckedChange={handleCheckboxChange}
        className="h-[18px] w-[18px] rounded border-border"
        data-action="checkbox"
      />

      {/* Thumbnail */}
      <div
        className={cn(
          'flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-lg',
          isGenerating ? 'bg-secondary-foreground' : 'bg-background-secondary'
        )}
      >
        {post.imageUrl ? (
          <img
            src={post.imageUrl}
            alt={post.postTitle}
            className="h-full w-full rounded-lg object-cover"
          />
        ) : isGenerating ? (
          <Loader2 className="h-6 w-6 animate-spin text-secondary" />
        ) : (
          <Image className="h-6 w-6 text-foreground-placeholder" />
        )}
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* Title Row */}
        <div className="flex items-center gap-2.5">
          <span className="truncate text-[15px] font-semibold text-foreground">
            {post.postTitle || post.hook?.slice(0, 50) || 'Untitled Post'}
          </span>

          {/* Status Badge */}
          {needsImage && (
            <span className="flex shrink-0 items-center gap-1 rounded bg-primary-foreground px-2 py-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span className="text-[11px] font-medium text-primary">Needs Image</span>
            </span>
          )}
          {isGenerating && (
            <span className="flex shrink-0 items-center gap-1 rounded bg-secondary-foreground px-2 py-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
              <span className="text-[11px] font-medium text-secondary">Generating...</span>
            </span>
          )}

          {/* Ready Status Badge */}
          {post.readyStatus === 'ready' ? (
            <Badge className="shrink-0 border-green-500/20 bg-green-500/10 text-green-600 dark:text-green-400">
              Ready to publish
            </Badge>
          ) : post.readyStatus === 'draft' ? (
            <Badge variant="secondary" className="shrink-0">
              Draft
            </Badge>
          ) : null}
        </div>

        {/* Description */}
        {post.hook && <p className="line-clamp-1 text-[13px] text-foreground-muted">{post.hook}</p>}

        {/* Meta */}
        <div className="flex items-center gap-2 text-[12px]">
          <span className="font-medium text-muted-foreground">{post.brandName}</span>
          <span className="text-foreground-placeholder">•</span>
          <span className="text-foreground-placeholder">{formatDate(post.generatedAt)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2" data-action="actions">
        {isGenerating ? (
          <>
            {/* Progress Bar */}
            <Progress
              value={post.generationProgress ?? 65}
              className="h-1.5 w-[100px] bg-secondary-foreground [&>[data-slot=progress-indicator]]:bg-secondary"
            />

            {/* Cancel Button */}
            <button
              onClick={handleCancel}
              className="rounded-md border border-border px-3 py-2 text-[12px] font-medium text-foreground-muted hover:bg-background-secondary"
              data-action="cancel"
            >
              Cancel
            </button>

            {/* Edit Button */}
            <button
              onClick={handleEdit}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background-card hover:bg-background-secondary"
              data-action="edit"
            >
              <Pencil className="h-3.5 w-3.5 text-foreground-muted" />
            </button>
          </>
        ) : (
          <>
            {/* Edit Button */}
            <button
              onClick={handleEdit}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background-card hover:bg-background-secondary"
              data-action="edit"
            >
              <Pencil className="h-3.5 w-3.5 text-foreground-muted" />
            </button>

            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background-card hover:bg-background-secondary"
              data-action="delete"
            >
              <Trash2 className="h-3.5 w-3.5 text-foreground-muted" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const PostCardMemo = React.memo(PostCard);

export { PostCardMemo };
